import { useEffect } from "react";
import styled from "styled-components";
import Head from "next/head";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { eventLog } from "../../firebase";

import { request } from "utils/request";

const ImageCarousel = dynamic(() => import("components/Detail/ImageCarousel"));
const Content = dynamic(() => import("components/Detail/Content"));
const FacilityInformation = dynamic(
  () => import("components/Detail/FacilityInformation")
);
const Comment = dynamic(() => import("components/Detail/Comment"));
const WriteComment = dynamic(() => import("components/Detail/WriteComment"));
const Facilities = dynamic(() => import("components/Detail/Facilities"));
const ProductList = dynamic(() => import("components/Detail/ProductList"));

interface Props {
  data: Camp;
  comments: any;
}

interface Camp {
  id: string;
  title: string;
  desc: string;
  imagePaths: string[];
  productList: any;
  city: string;
  county: string;
  rating: number;
  lat: number;
  long: number;
  slug: string;
  facilities: any;
  isFacility: boolean;
  phone?: string;
  web?: string;
  socialMedia?: string;
}

const Detail = (props: Props) => {
  const router = useRouter();

  const pageTitle = `${props.data?.title} - Kampp`;
  const pageUrl = `https://kampp.in/kamp-alani/${props.data.slug}`;

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      eventLog("camp_detail", { target: props.data?.title });
    }
  }, []);

  return (
    <Container>
      <Head>
        <title>{pageTitle}</title>
        <link rel="icon" href="/favicon.ico" />
        <meta name="title" property="og:title" content={pageTitle} />
        <meta
          name="description"
          property="og:description"
          content={
            props.data?.desc
              ? props.data?.desc.substring(0, 140)
              : props.data.title
          }
        ></meta>
        <meta
          name="image"
          property="og:image"
          content={props.data?.imagePaths[0]}
        />
        <meta name="url" property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta
          property="og:description"
          content={
            props.data?.desc
              ? props.data.desc.substring(0, 140)
              : props.data.title
          }
        ></meta>
        <meta property="og:image" content={props.data?.imagePaths[0]} />
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="600" />
        <meta
          property="og:image:alt"
          content={props.data?.slug || props.data?.title}
        />
        <meta property="og:site_name" content="Kampp - Kamppla Planla!" />
        <meta property="og:locale" content="tr_TR" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary" />
        <meta property="twitter:title" content={pageTitle} />
        <meta
          property="twitter:description"
          content={
            props.data?.desc
              ? props.data?.desc.substring(0, 140)
              : props.data.title
          }
        />
        <meta property="twitter:url" content={pageUrl} />
      </Head>

      <InnerContainer>
        <ImageCarousel images={props.data?.imagePaths} />
        <Content
          id={props.data?.id}
          title={props.data?.title}
          city={props.data?.city}
          county={props.data?.county}
          rating={props.data?.rating}
        />

        {props.data?.isFacility && (
          <ItemContainer>
            <SectionTitle>Tesis Bilgileri</SectionTitle>
            <FacilityInformation
              id={props.data?.id}
              phone={props.data?.phone}
              web={props.data?.web}
              socialMedia={props.data?.socialMedia}
            />
          </ItemContainer>
        )}

        <ItemContainer>
          <Description>{props.data?.desc}</Description>
        </ItemContainer>

        {props.data?.facilities?.length > 0 && (
          <ItemContainer>
            <SectionTitle>Alanın Olanakları</SectionTitle>
            <Facilities data={props.data?.facilities} />
          </ItemContainer>
        )}

        {props.data.productList?.length > 0 && (
          <ItemContainer>
            <SectionTitle>Bu Ürünlere İhtiyacın Olabilir</SectionTitle>
            <ProductList data={props.data.productList} />
          </ItemContainer>
        )}

        <ItemContainer>
          <SectionTitle>Nerede Olacaksınız?</SectionTitle>
          <StyledMap
            width={640}
            height={320}
            src={`https://maps.google.com/maps?q=${props.data.lat},${props.data.long}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
          ></StyledMap>
        </ItemContainer>

        {props.comments?.data?.length > 0 && (
          <ItemContainer>
            <SectionTitle>Yorumlar</SectionTitle>
            <Comment data={props.comments?.data} />
          </ItemContainer>
        )}

        <ItemContainer>
          <SectionTitle>Yorum Yap</SectionTitle>
          <WriteComment campId={props.data.id} />
        </ItemContainer>
      </InnerContainer>
    </Container>
  );
};

// export async function getStaticPaths() {
//   const { data }: any = await request("camp/getAllCampRecords", "GET");

//   const paths = data.map((camp: any) => ({
//     params: { slug: camp.slug.toString(), id: camp.id },
//   }))

//   return { paths, fallback: false }
// }

// export async function getStaticProps({ params }: any) {
//   const { data } = await request(`camp/getCampRecord/${params.slug}`, "GET");
//   // const commentResponse = await request(`comment/${params.id}`, "GET");
//   return { props: { data: data, comments: [] } };
// }

export async function getServerSideProps(context: any) {
  const { slug } = context.query;

  const { data } = await request(`camp/getCampRecord/${slug}`, "GET");
  const commentResponse = await request(`comment/${data.id}`, "GET");
  return { props: { data: data, comments: commentResponse.data } };
}

export default Detail;

const Container = styled.div`
  padding: 65px 30%;
  @media (max-width: ${(props) => props.theme.size.laptopL}) {
    padding: 65px 15%;
  }
  @media (max-width: ${(props) => props.theme.size.tablet}) {
    padding: 65px 30px;
  }
`;

const InnerContainer = styled.div`
  padding-top 24px;
`;

const ItemContainer = styled.div`
  padding: 32px 0px;
  border-bottom: 1px solid #dadada;
`;

const Description = styled.span`
  line-height: 25px;
  color: #222222;
  font-size: 16px;
  font-weight: 400;
  white-space: break-spaces;
`;

const SectionTitle = styled.span`
  color: #222222;
  font-size: 24px;
  font-weight: 500;
`;

const StyledMap = styled.iframe`
  width: 100%;
  height: 350px;
  border: none;
  margin-top: 32px;
`;
